// Web Push en el front: soporte, permiso, suscripción con VAPID y registro en el backend.
// Spec: docs/2026-08-25-push-inactividad-pwa-spec.md
//
// OJO dev: el SW se DESREGISTRA en `npm run dev` (main.tsx) → `serviceWorker.ready` no resuelve.
// El testing de push se hace con un build servido (vite preview) o en producción.

import { VAPID_PUBLIC_KEY } from '../config';
import { atlasBackendFetch } from './atlasBackend';

/** VAPID public key (base64url) → Uint8Array, como exige pushManager.subscribe. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export type PushSoporte =
  | 'ok' // se puede pedir/usar push
  | 'sin-soporte' // el navegador no tiene SW/Push/Notification
  | 'ios-no-instalada'; // iOS pero la PWA no está instalada → no hay push posible

/** ¿Este navegador/dispositivo puede recibir push ahora? */
export function soportePush(): PushSoporte {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'sin-soporte';
  }
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (isIOS && !standalone) return 'ios-no-instalada';
  return 'ok';
}

/** Estado actual del permiso del navegador. */
export function permisoPush(): NotificationPermission | 'unsupported' {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Devuelve el `ServiceWorkerRegistration` activo, o null si no hay SW usable.
 *
 * Clave: primero preguntamos si HAY un registro (`getRegistration`). En `npm run dev` el SW se
 * DESREGISTRA al arrancar (main.tsx) → no hay registro → devolvemos null sin esperar `ready` (que
 * nunca resolvería y colgaría la UI). En producción SÍ hay registro → esperamos a que active con
 * `ready`. NO usamos un timeout corto: una primera instalación/actualización lenta (>3s) no debe
 * falsear "no disponible". El timeout largo es solo un cinturón anti-cuelgue por si un SW registrado
 * jamás llegara a activar (caso patológico).
 */
async function swReady(timeoutMs = 20000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existente = await navigator.serviceWorker.getRegistration().catch(() => null);
  if (!existente) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

export interface ActivarResultado {
  ok: boolean;
  /** 'sin-soporte' | 'ios-no-instalada' | 'permiso-denegado' | 'sw-no-disponible' | 'backend-<status>' | 'error' */
  motivo?: string;
}

/**
 * Pide permiso (si hace falta), suscribe con VAPID y registra la subscription en el backend.
 * DEBE llamarse desde un gesto del usuario (click). Idempotente: reusa la subscription existente.
 */
export async function activarPush(): Promise<ActivarResultado> {
  const soporte = soportePush();
  if (soporte !== 'ok') return { ok: false, motivo: soporte };

  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return { ok: false, motivo: 'permiso-denegado' };

  let sub: PushSubscription;
  let eraNueva = false;
  try {
    const reg = await swReady();
    if (!reg) return { ok: false, motivo: 'sw-no-disponible' };
    const existente = await reg.pushManager.getSubscription();
    if (existente) {
      sub = existente;
    } else {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      eraNueva = true;
    }
  } catch (err) {
    return { ok: false, motivo: err instanceof Error ? err.message : 'error' };
  }

  const json = sub.toJSON();
  if (!json.keys?.p256dh || !json.keys?.auth) {
    if (eraNueva) await sub.unsubscribe().catch(() => {});
    return { ok: false, motivo: 'error' };
  }

  // Registrar en el backend. Si falla y la subscription la creamos ACÁ, la revertimos: así el estado
  // no queda "activado" en falso (sub local sin registro en el backend → nunca llegarían pushes).
  // Si la subscription ya existía (el backend probablemente ya la tiene), NO la tocamos.
  try {
    const res = await atlasBackendFetch('/v1/push/subscribe', {
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    if (!res.ok) {
      if (eraNueva) await sub.unsubscribe().catch(() => {});
      return { ok: false, motivo: `backend-${res.status}` };
    }
  } catch (err) {
    if (eraNueva) await sub.unsubscribe().catch(() => {});
    return { ok: false, motivo: err instanceof Error ? err.message : 'backend-error' };
  }
  return { ok: true };
}

/** Da de baja la subscription local y avisa al backend (best-effort). */
export async function desactivarPush(): Promise<void> {
  try {
    const reg = await swReady();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await atlasBackendFetch('/v1/push/unsubscribe', { endpoint }).catch(() => {});
  } catch {
    /* best-effort */
  }
}

/** ¿Ya está suscripto en este dispositivo? (para reflejar el estado en la UI) */
export async function estaSuscripto(): Promise<boolean> {
  if (soportePush() !== 'ok' || Notification.permission !== 'granted') return false;
  try {
    const reg = await swReady();
    if (!reg) return false;
    return (await reg.pushManager.getSubscription()) != null;
  } catch {
    return false;
  }
}
